import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { TrendingUp, TrendingDown, Loader2, Settings, Euro, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENT_YM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const MINUS_6_YM = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 5);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const fmtMonth = (ym) => {
  const [y, m] = ym.split('-');
  const names = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Déc'];
  return `${names[parseInt(m) - 1]} ${y.slice(2)}`;
};

const fmtEur = (v) => (v ?? 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

export default function ROISection({ clients, onSettingsUpdated }) {
  const [start, setStart] = useState(MINUS_6_YM());
  const [end, setEnd] = useState(CURRENT_YM());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState({ lead_value: 0, monthly_marketing_cost: 0 });
  const [saving, setSaving] = useState(false);

  const fetchROI = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(
        `${API}/settings/leads/roi?start=${start}&end=${end}`,
        { withCredentials: true }
      );
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement ROI');
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    fetchROI();
  }, [fetchROI]);

  const openEdit = (row) => {
    setEditingClient(row);
    setForm({
      lead_value: row.lead_value || 0,
      monthly_marketing_cost: row.monthly_marketing_cost || 0,
    });
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(
        `${API}/clients/${editingClient.client_id}/stats-settings`,
        {
          lead_value: parseFloat(form.lead_value) || 0,
          monthly_marketing_cost: parseFloat(form.monthly_marketing_cost) || 0,
        },
        { withCredentials: true }
      );
      toast.success('Paramètres enregistrés');
      setEditingClient(null);
      fetchROI();
      if (onSettingsUpdated) onSettingsUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = () => {
    if (!data) return;
    const headers = ['Client', 'Valeur lead (€)', 'Coût mensuel (€)', ...data.month_labels.map(fmtMonth), 'Total leads', 'Revenu (€)', 'Coût total (€)', 'Net (€)', 'ROI %'];
    const rows = data.rows.map(r => [
      r.client_name, r.lead_value, r.monthly_marketing_cost,
      ...r.monthly.map(m => m.count),
      r.total_leads, r.revenue, r.cost, r.net, r.roi_pct ?? '',
    ]);
    const escape = v => { const s = String(v ?? ''); return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = [headers, ...rows].map(r => r.map(escape).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rentabilite_${start}_${end}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-5 space-y-4" data-testid="roi-section">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h2 className="font-heading font-semibold text-lg text-neutral-900 flex items-center gap-2">
            <Euro className="w-5 h-5 text-[#002FA7]" /> Rentabilité marketing par client
          </h2>
          <p className="text-xs text-neutral-500 mt-1">Historique illimité. Définissez une valeur par lead et un coût marketing mensuel pour chaque client.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label htmlFor="roi-start" className="text-xs text-neutral-600">Début</Label>
            <Input id="roi-start" type="month" value={start} onChange={e => setStart(e.target.value)}
                   className="h-9 text-sm" data-testid="roi-start" />
          </div>
          <div>
            <Label htmlFor="roi-end" className="text-xs text-neutral-600">Fin</Label>
            <Input id="roi-end" type="month" value={end} onChange={e => setEnd(e.target.value)}
                   className="h-9 text-sm" data-testid="roi-end" />
          </div>
          <Button size="sm" variant="outline" onClick={exportCSV} disabled={!data || loading} data-testid="roi-export-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#002FA7]" />
        </div>
      ) : !data || data.rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucune donnée sur cette période. Calculez les mois correspondants dans la section ci-dessus.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
            <MiniStat label="Total leads" value={data.totals.total_leads} mono />
            <MiniStat label="Revenu" value={fmtEur(data.totals.revenue)} color="text-[#2A9D8F]" />
            <MiniStat label="Coût" value={fmtEur(data.totals.cost)} color="text-[#E76F51]" />
            <MiniStat label="ROI net"
                      value={fmtEur(data.totals.net)}
                      color={data.totals.net >= 0 ? 'text-[#002FA7]' : 'text-red-600'}
                      extra={data.totals.roi_pct != null ? `${data.totals.roi_pct > 0 ? '+' : ''}${data.totals.roi_pct}%` : null} />
          </div>

          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-neutral-500 text-xs">
                  <th className="pb-2 pr-3">Client</th>
                  <th className="pb-2 pr-2 text-right">Valeur lead</th>
                  <th className="pb-2 pr-2 text-right">Coût mens.</th>
                  {data.month_labels.map(m => (
                    <th key={m} className="pb-2 px-1 text-right font-normal">{fmtMonth(m)}</th>
                  ))}
                  <th className="pb-2 px-2 text-right">Leads</th>
                  <th className="pb-2 px-2 text-right text-[#2A9D8F]">Revenu</th>
                  <th className="pb-2 px-2 text-right text-[#E76F51]">Coût</th>
                  <th className="pb-2 px-2 text-right font-bold">Net</th>
                  <th className="pb-2 px-2 text-right">ROI %</th>
                  <th className="pb-2 pl-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.client_id} className="border-b border-neutral-50 hover:bg-neutral-50"
                      data-testid={`roi-row-${r.client_id}`}>
                    <td className="py-2.5 pr-3 font-medium">{r.client_name}</td>
                    <td className="py-2.5 pr-2 text-right font-mono text-xs">{fmtEur(r.lead_value)}</td>
                    <td className="py-2.5 pr-2 text-right font-mono text-xs">{fmtEur(r.monthly_marketing_cost)}</td>
                    {r.monthly.map((m, idx) => (
                      <td key={data.month_labels[idx]} className="py-2.5 px-1 text-right font-mono">
                        {m.count > 0 ? <span className="text-[#002FA7] font-medium">{m.count}</span> : <span className="text-neutral-300">0</span>}
                      </td>
                    ))}
                    <td className="py-2.5 px-2 text-right font-mono font-semibold">{r.total_leads}</td>
                    <td className="py-2.5 px-2 text-right font-mono text-[#2A9D8F]">{fmtEur(r.revenue)}</td>
                    <td className="py-2.5 px-2 text-right font-mono text-[#E76F51]">{fmtEur(r.cost)}</td>
                    <td className={`py-2.5 px-2 text-right font-mono font-bold ${r.net >= 0 ? 'text-[#002FA7]' : 'text-red-600'}`}>
                      {fmtEur(r.net)}
                    </td>
                    <td className="py-2.5 px-2 text-right text-xs">
                      {r.roi_pct == null ? (
                        <span className="text-neutral-300">—</span>
                      ) : (
                        <span className={`inline-flex items-center gap-0.5 font-medium ${r.roi_pct >= 0 ? 'text-[#2A9D8F]' : 'text-red-600'}`}>
                          {r.roi_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {r.roi_pct > 0 ? '+' : ''}{r.roi_pct}%
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pl-2 text-right">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                              onClick={() => openEdit(r)} data-testid={`roi-edit-${r.client_id}`}
                              title="Configurer valeur et coût">
                        <Settings className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={!!editingClient} onOpenChange={o => { if (!o) setEditingClient(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rentabilité — {editingClient?.client_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveSettings} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="lead-value">Valeur d'un lead (€)</Label>
              <Input id="lead-value" type="number" min="0" step="0.01" value={form.lead_value}
                     onChange={e => setForm(f => ({ ...f, lead_value: e.target.value }))}
                     placeholder="Ex: 250"
                     data-testid="input-lead-value" />
              <p className="text-xs text-neutral-500 mt-1">Revenu moyen estimé par lead converti.</p>
            </div>
            <div>
              <Label htmlFor="mk-cost">Coût marketing mensuel (€)</Label>
              <Input id="mk-cost" type="number" min="0" step="0.01" value={form.monthly_marketing_cost}
                     onChange={e => setForm(f => ({ ...f, monthly_marketing_cost: e.target.value }))}
                     placeholder="Ex: 1500"
                     data-testid="input-mk-cost" />
              <p className="text-xs text-neutral-500 mt-1">Dépense mensuelle (Ads, SEO, prestataires…).</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditingClient(null)}>Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002277]" data-testid="roi-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Enregistrer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ label, value, color = 'text-neutral-800', mono = false, extra = null }) {
  return (
    <div className="bg-neutral-50 rounded-md p-3">
      <p className="text-xs text-neutral-500">{label}</p>
      <p className={`text-lg font-bold mt-0.5 ${color} ${mono ? 'font-mono' : ''}`}>
        {value}
      </p>
      {extra && <p className={`text-xs ${color}`}>{extra}</p>}
    </div>
  );
}
